import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface UserPreferences {
  id: string;
  user_id: string;
  onboarding_completed_at: string | null;
  onboarding_skipped: boolean;
  default_report_standard: string | null;
  default_jurisdiction_state: string | null;
  default_additional_requirements: string | null;
  default_site_address: string | null;
  default_site_city: string | null;
  default_site_state: string | null;
  default_site_zip: string | null;
  default_site_lat: number | null;
  default_site_lng: number | null;
  dashboard_layout: Record<string, unknown> | null;
}

export type UserPreferencesPatch = Partial<
  Omit<UserPreferences, "id" | "user_id">
>;

/**
 * Loads (and lazily caches) the current user's preferences row. Returns a
 * `save()` helper that does an upsert keyed on user_id, so callers don't
 * need to worry about whether a row exists yet.
 */
export function useUserPreferences() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<UserPreferences | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isMissing, setIsMissing] = useState(false);

  useEffect(() => {
    if (!user) {
      setPrefs(null);
      setIsReady(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.error("[useUserPreferences] load failed", error);
      }
      setPrefs((data as UserPreferences | null) ?? null);
      setIsMissing(!data);
      setIsReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const save = useCallback(
    async (patch: UserPreferencesPatch) => {
      if (!user) throw new Error("Not signed in");
      const payload = { user_id: user.id, ...patch } as never;
      const { data, error } = await supabase
        .from("user_preferences")
        .upsert([payload], { onConflict: "user_id" })
        .select()
        .maybeSingle();
      if (error) throw error;
      const next = (data as UserPreferences) ?? null;
      setPrefs(next);
      setIsMissing(!next);
      return next;
    },
    [user],
  );

  return { prefs, isReady, isMissing, save };
}
